/**
 * Hijri formatting helpers (M3X.UI-HYGIENE-PASS-2 PR 2).
 *
 * Uses `Intl.DateTimeFormat` with `calendar=islamic-umalqura` —
 * the SAMA-aligned Umm al-Qura calendar. No custom algorithm; the
 * Intl polyfill in modern V8 / Safari ships authoritative conversion.
 */

/** Format a Gregorian Date as Umm al-Qura (Hijri) per Saudi Arabia. */
export function formatHijri(
  date: Date | string | null | undefined,
  options: { locale?: "en-US" | "ar-SA"; month?: "long" | "short" | "numeric" } = {},
): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";

  const locale = options.locale ?? "en-US";
  const monthFmt = options.month ?? "long";

  try {
    // `-u-ca-islamic-umalqura` Locale extension chooses the
    // Umm al-Qura calendar; this is the official Saudi calendar.
    return new Intl.DateTimeFormat(`${locale}-u-ca-islamic-umalqura`, {
      year: "numeric",
      month: monthFmt,
      day: "numeric",
    }).format(d);
  } catch {
    return "";
  }
}

/** Same value formatted as both Gregorian + Hijri for tooltips. */
export function dualCalendarLabel(
  date: Date | string | null | undefined,
  locale: "en-US" | "ar-SA" = "en-US",
): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";

  const greg = new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(d);
  const hij = formatHijri(d, { locale, month: "short" });
  return hij ? `${greg} · ${hij}` : greg;
}
